import React, { useEffect, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Divider,
  Stack,
  CircularProgress,
} from "@mui/material";

const FullChangePanel = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [items, setItems] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [selected, setSelected] = useState(null);

  const [newTitle, setNewTitle] = useState("");
  const [newDescription, setNewDescription] = useState("");

  const [linkedLoading, setLinkedLoading] = useState(false);
  const [linkedError, setLinkedError] = useState(null);
  const [linkedChanges, setLinkedChanges] = useState([]);

  const [assignDevIdeChangeId, setAssignDevIdeChangeId] = useState("");
  const [assignOrder, setAssignOrder] = useState("");

  const [plannerRunsLoading, setPlannerRunsLoading] = useState(false);
  const [plannerRunsError, setPlannerRunsError] = useState(null);
  const [plannerRuns, setPlannerRuns] = useState([]);

  // NEW: create planner run from Full Change
  const [plannerCreateLoading, setPlannerCreateLoading] = useState(false);
  const [plannerCreateError, setPlannerCreateError] = useState(null);
  const [plannerModule, setPlannerModule] = useState("devtools");
  const [plannerGoalText, setPlannerGoalText] = useState("");

  // NEW: build context text to store as a planner goal (no schema changes required)
  const buildFullChangeContextText = (fc, linked, fallbackGoalText) => {
    const title = fc && fc.title ? String(fc.title) : "";
    const desc = fc && fc.description ? String(fc.description) : "";

    const lines = [];
    lines.push("FULL CHANGE CONTEXT");
    lines.push("");
    if (fc && fc.id !== undefined && fc.id !== null) lines.push(`Full Change ID: ${fc.id}`);
    if (title) lines.push(`Title: ${title}`);
    if (fc && fc.status) lines.push(`Status: ${fc.status}`);
    if (fc && fc.priority) lines.push(`Priority: ${fc.priority}`);
    if (fc && fc.owner) lines.push(`Owner: ${fc.owner}`);

    if (desc) {
      lines.push("");
      lines.push("Description:");
      lines.push(desc);
    }

    if (linked && Array.isArray(linked) && linked.length > 0) {
      lines.push("");
      lines.push("Linked Dev IDE Changes:");
      linked.forEach((c) => {
        const cid = c && c.id !== undefined && c.id !== null ? `#${c.id}` : "#?";
        const ctitle = c && c.title ? String(c.title) : "";
        const cstatus = c && c.status ? String(c.status) : "";
        const ord =
          c && c.full_change_order !== null && c.full_change_order !== undefined
            ? ` (order ${c.full_change_order})`
            : "";
        lines.push(`- ${cid}${ord} ${ctitle}${cstatus ? ` — ${cstatus}` : ""}`.trim());
      });
    }

    const goal = String(fallbackGoalText || "").trim();
    if (goal) {
      lines.push("");
      lines.push("Planner Goal Text:");
      lines.push(goal);
    }

    return lines.join("\n").trim();
  };

  const loadList = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch("/api/dev-ide/full-change/list");
      const data = await res.json();
      if (!data.success) throw new Error(data.error || "Failed to load full changes.");
      setItems(data.full_changes || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const loadOne = async (id) => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`/api/dev-ide/full-change/get?id=${encodeURIComponent(id)}`);
      const data = await res.json();
      if (!data.success) throw new Error(data.error || "Failed to load full change.");
      setSelected(data.full_change || null);

      // NEW: seed planner goal text from Full Change (only if empty)
      const fc = data.full_change || null;
      const seedTitle = fc && fc.title ? String(fc.title) : "";
      const seedDesc = fc && fc.description ? String(fc.description) : "";
      const seed = `${seedTitle}${seedDesc ? `\n\n${seedDesc}` : ""}`.trim();
      setPlannerGoalText((prev) => (prev && String(prev).trim().length > 0 ? prev : seed));
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const loadLinked = async (fullChangeId) => {
    if (!fullChangeId) {
      setLinkedChanges([]);
      return;
    }
    setLinkedLoading(true);
    setLinkedError(null);
    try {
      const res = await fetch(
        `/api/dev-ide/full-change/linked-changes?full_change_id=${encodeURIComponent(fullChangeId)}`
      );
      const data = await res.json();
      if (!data.success) throw new Error(data.error || "Failed to load linked changes.");
      setLinkedChanges(data.linked_changes || []);
    } catch (e) {
      setLinkedError(e.message);
    } finally {
      setLinkedLoading(false);
    }
  };

  const loadPlannerRuns = async (fullChangeId) => {
    if (!fullChangeId) {
      setPlannerRuns([]);
      return;
    }
    setPlannerRunsLoading(true);
    setPlannerRunsError(null);
    try {
      const res = await fetch(
        `/api/dev-ide/planner/runs?full_change_id=${encodeURIComponent(
          fullChangeId
        )}&limit=200&offset=0`
      );
      const data = await res.json();
      if (!data.success) throw new Error(data.error || "Failed to load planner runs.");
      setPlannerRuns(data.runs || []);
    } catch (e) {
      setPlannerRunsError(e.message);
      setPlannerRuns([]);
    } finally {
      setPlannerRunsLoading(false);
    }
  };

  useEffect(() => {
    loadList();
  }, []);

  useEffect(() => {
    if (selectedId) {
      setPlannerCreateError(null);
      loadOne(selectedId);
      loadLinked(selectedId);
      loadPlannerRuns(selectedId);
    } else {
      setSelected(null);
      setLinkedChanges([]);
      setPlannerRuns([]);

      // NEW: reset planner draft inputs when nothing selected
      setPlannerCreateError(null);
      setPlannerGoalText("");
    }
  }, [selectedId]);

  const handleCreate = async () => {
    const title = (newTitle || "").trim();
    if (!title) {
      setError("Title is required.");
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const res = await fetch("/api/dev-ide/full-change/create", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          title,
          description: newDescription || "",
          status: "DRAFT",
        }),
      });
      const data = await res.json();
      if (!data.success) {
        throw new Error((data.errors && data.errors.join(", ")) || data.error || "Create failed.");
      }

      setNewTitle("");
      setNewDescription("");
      await loadList();

      if (data.full_change && data.full_change.id) {
        setSelectedId(data.full_change.id);
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateSelected = async () => {
    if (!selected || !selected.id) return;

    setLoading(true);
    setError(null);
    try {
      const res = await fetch("/api/dev-ide/full-change/update", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          id: selected.id,
          title: selected.title,
          description: selected.description,
          status: selected.status,
          priority: selected.priority,
          owner: selected.owner,
        }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error || "Update failed.");

      setSelected(data.full_change || selected);
      await loadList();
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAssign = async () => {
    if (!selectedId) return;

    const devId = String(assignDevIdeChangeId || "").trim();
    if (!devId) {
      setLinkedError("dev_ide_change_id is required.");
      return;
    }

    const orderVal = String(assignOrder || "").trim();
    const payload = {
      full_change_id: selectedId,
      dev_ide_change_id: devId,
    };
    if (orderVal !== "") {
      payload.full_change_order = orderVal;
    }

    setLinkedLoading(true);
    setLinkedError(null);
    try {
      const res = await fetch("/api/dev-ide/full-change/assign-dev-ide-change", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!data.success) {
        throw new Error((data.errors && data.errors.join(", ")) || data.error || "Assign failed.");
      }

      setAssignDevIdeChangeId("");
      setAssignOrder("");
      await loadLinked(selectedId);
    } catch (e) {
      setLinkedError(e.message);
    } finally {
      setLinkedLoading(false);
    }
  };

  const handleUnassign = async (devIdeChangeId) => {
    if (!devIdeChangeId) return;

    setLinkedLoading(true);
    setLinkedError(null);
    try {
      const res = await fetch("/api/dev-ide/full-change/unassign-dev-ide-change", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ dev_ide_change_id: devIdeChangeId }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error || "Unassign failed.");
      await loadLinked(selectedId);
    } catch (e) {
      setLinkedError(e.message);
    } finally {
      setLinkedLoading(false);
    }
  };

  // NEW: create a planner run linked to this Full Change (DRAFT)
  // FIX: create planner_session_id first, then create the run
  const handleCreatePlannerRun = async () => {
    if (!selectedId) return;

    const mod = String(plannerModule || "").trim();
    if (!mod) {
      setPlannerCreateError("Module is required.");
      return;
    }

    const goal = String(plannerGoalText || "").trim();
    if (!goal) {
      setPlannerCreateError("Planner goal text is required.");
      return;
    }

    setPlannerCreateLoading(true);
    setPlannerCreateError(null);
    try {
      // Step 1: create planner session
      const sessRes = await fetch("/api/dev-ide/planner/sessions", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          module: mod,
          full_change_id: selectedId,
        }),
      });
      const sessData = await sessRes.json();
      if (!sessData.success) {
        throw new Error(
          (sessData.errors && sessData.errors.join(", ")) ||
            sessData.error ||
            "Create planner session failed."
        );
      }

      // FIX (minimal): backend returns session_id
      const plannerSessionId =
        sessData.planner_session_id ??
        sessData.session_id ??
        sessData.sessionId ??
        (sessData.session && sessData.session.id) ??
        null;

      if (plannerSessionId === null || plannerSessionId === undefined || plannerSessionId === "") {
        throw new Error("Create planner session did not return planner_session_id.");
      }

      // Step 2: create planner run (now includes planner_session_id)
      const res = await fetch("/api/dev-ide/planner/runs", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          planner_session_id: plannerSessionId,
          full_change_id: selectedId,
          module: mod,
          status: "DRAFT",
          goal_text: goal,
        }),
      });
      const data = await res.json();
      if (!data.success) {
        throw new Error(
          (data.errors && data.errors.join(", ")) || data.error || "Create planner run failed."
        );
      }

      // NEW (minimal): also create a planner goal that contains full change context
      // This avoids planner input schema changes for now, but ensures context is carried.
      try {
        const contextText = buildFullChangeContextText(selected, linkedChanges, goal);

        await fetch("/api/dev-ide/planner/goals", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            planner_session_id: plannerSessionId,
            category: "FULL_CHANGE",
            description: "Full Change context captured at run creation time.",
            active: true,
            goal_text: contextText,
          }),
        });
      } catch (e) {
        // Do not fail the main flow if goal creation fails (planner run still created)
      }

      await loadPlannerRuns(selectedId);
    } catch (e) {
      setPlannerCreateError(e.message);
    } finally {
      setPlannerCreateLoading(false);
    }
  };

  return (
    <Box sx={{ p: 2, height: "100%", minHeight: 0 }}>
      <Stack direction="row" spacing={2} sx={{ height: "100%", minHeight: 0 }}>
        {/* Left list */}
        <Paper sx={{ width: 360, p: 2, overflowY: "auto" }}>
          <Typography variant="h6">Full Change</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 2 }}>
            Master change record (dev_ide_full_changes) linked via dev_ide_changes.full_change_id
          </Typography>

          <Divider sx={{ mb: 2 }} />

          <Typography variant="subtitle2">Create</Typography>
          <TextField
            size="small"
            fullWidth
            label="Title"
            value={newTitle}
            onChange={(e) => setNewTitle(e.target.value)}
            sx={{ mt: 1 }}
          />
          <TextField
            size="small"
            fullWidth
            label="Description"
            value={newDescription}
            onChange={(e) => setNewDescription(e.target.value)}
            sx={{ mt: 1 }}
            multiline
            minRows={3}
          />
          <Button variant="contained" onClick={handleCreate} sx={{ mt: 1 }} disabled={loading}>
            {loading ? "Working..." : "Create Full Change"}
          </Button>

          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Existing
          </Typography>

          {loading && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
              <CircularProgress size={22} />
            </Box>
          )}

          {error && (
            <Typography color="error" variant="body2" sx={{ mb: 1 }}>
              {error}
            </Typography>
          )}

          {(items || []).map((it) => (
            <Box
              key={it.id}
              onClick={() => setSelectedId(it.id)}
              sx={{
                p: 1,
                borderRadius: 1,
                cursor: "pointer",
                border: "1px solid rgba(0,0,0,0.12)",
                mb: 1,
                backgroundColor: selectedId === it.id ? "rgba(25,118,210,0.08)" : "transparent",
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                #{it.id} {it.title}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {it.status || "DRAFT"}
              </Typography>
            </Box>
          ))}
        </Paper>

        {/* Right details */}
        <Paper sx={{ flex: 1, p: 2, display: "flex", flexDirection: "column", minHeight: 0 }}>
          {!selectedId ? (
            <Typography variant="body2" color="text.secondary">
              Select a Full Change on the left.
            </Typography>
          ) : (
            <>
              <Typography variant="h6" sx={{ mb: 1 }}>
                Full Change Details
              </Typography>

              <TextField
                size="small"
                fullWidth
                label="Title"
                value={(selected && selected.title) || ""}
                onChange={(e) =>
                  setSelected((prev) => ({ ...(prev || {}), title: e.target.value }))
                }
                sx={{ mb: 1 }}
              />

              <TextField
                size="small"
                fullWidth
                label="Description"
                value={(selected && selected.description) || ""}
                onChange={(e) =>
                  setSelected((prev) => ({ ...(prev || {}), description: e.target.value }))
                }
                sx={{ mb: 1 }}
                multiline
                minRows={4}
              />

              <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                <TextField
                  size="small"
                  fullWidth
                  label="Status"
                  value={(selected && selected.status) || "DRAFT"}
                  onChange={(e) =>
                    setSelected((prev) => ({ ...(prev || {}), status: e.target.value }))
                  }
                  helperText="DRAFT | PLANNED | IN_PROGRESS | READY_FOR_TEST | APPROVED | RELEASED"
                />
              </Stack>

              <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                <TextField
                  size="small"
                  fullWidth
                  label="Priority"
                  value={(selected && selected.priority) || ""}
                  onChange={(e) =>
                    setSelected((prev) => ({ ...(prev || {}), priority: e.target.value }))
                  }
                />
                <TextField
                  size="small"
                  fullWidth
                  label="Owner"
                  value={(selected && selected.owner) || ""}
                  onChange={(e) =>
                    setSelected((prev) => ({ ...(prev || {}), owner: e.target.value }))
                  }
                />
              </Stack>

              <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                <Button variant="contained" onClick={handleUpdateSelected} disabled={loading}>
                  {loading ? "Working..." : "Save"}
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => loadLinked(selectedId)}
                  disabled={linkedLoading}
                >
                  Refresh Linked Changes
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => loadPlannerRuns(selectedId)}
                  disabled={plannerRunsLoading}
                >
                  Refresh Planner Runs
                </Button>
              </Stack>

              <Divider sx={{ mb: 2 }} />

              {/* NEW: Create Planner Run (DRAFT) */}
              <Typography variant="subtitle1">Create Planner Run (DRAFT)</Typography>

              <Stack direction="row" spacing={1} sx={{ mt: 1, mb: 1, flexWrap: "wrap" }}>
                <TextField
                  size="small"
                  label="module"
                  value={plannerModule}
                  onChange={(e) => setPlannerModule(e.target.value)}
                />
                <Button
                  variant="outlined"
                  onClick={handleCreatePlannerRun}
                  disabled={plannerCreateLoading}
                >
                  {plannerCreateLoading ? "Working..." : "Create Planner Run"}
                </Button>
              </Stack>

              <TextField
                size="small"
                fullWidth
                label="planner goal text"
                value={plannerGoalText}
                onChange={(e) => setPlannerGoalText(e.target.value)}
                multiline
                minRows={4}
                sx={{ mb: 1 }}
              />

              {plannerCreateError && (
                <Typography color="error" variant="body2" sx={{ mb: 1 }}>
                  {plannerCreateError}
                </Typography>
              )}

              <Divider sx={{ mb: 2 }} />

              <Typography variant="subtitle1">Linked Dev IDE changes</Typography>

              <Stack direction="row" spacing={1} sx={{ mt: 1, mb: 1, flexWrap: "wrap" }}>
                <TextField
                  size="small"
                  label="dev_ide_change_id"
                  value={assignDevIdeChangeId}
                  onChange={(e) => setAssignDevIdeChangeId(e.target.value)}
                />
                <TextField
                  size="small"
                  label="order (optional)"
                  value={assignOrder}
                  onChange={(e) => setAssignOrder(e.target.value)}
                />
                <Button variant="outlined" onClick={handleAssign} disabled={linkedLoading}>
                  Assign
                </Button>
              </Stack>

              {linkedError && (
                <Typography color="error" variant="body2" sx={{ mb: 1 }}>
                  {linkedError}
                </Typography>
              )}

              <Box
                sx={{
                  border: "1px solid rgba(0,0,0,0.12)",
                  borderRadius: 1,
                  p: 1,
                  mb: 2,
                }}
              >
                {linkedLoading && (
                  <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                    <CircularProgress size={20} />
                  </Box>
                )}

                {!linkedLoading && (!linkedChanges || linkedChanges.length === 0) && (
                  <Typography variant="caption" color="text.secondary">
                    No linked Dev IDE changes yet.
                  </Typography>
                )}

                {(linkedChanges || []).map((c) => (
                  <Box
                    key={c.id}
                    sx={{
                      p: 1,
                      borderBottom: "1px solid rgba(0,0,0,0.08)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 1,
                    }}
                  >
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        Change #{c.id} — {c.title || ""}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {c.status || ""}
                        {c.full_change_order !== null && c.full_change_order !== undefined
                          ? ` — order ${c.full_change_order}`
                          : ""}
                      </Typography>
                    </Box>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => handleUnassign(c.id)}
                      disabled={linkedLoading}
                    >
                      Unassign
                    </Button>
                  </Box>
                ))}
              </Box>

              <Divider sx={{ mb: 2 }} />

              <Typography variant="subtitle1">Linked Planner Runs</Typography>

              {plannerRunsError && (
                <Typography color="error" variant="body2" sx={{ mb: 1 }}>
                  {plannerRunsError}
                </Typography>
              )}

              <Box
                sx={{
                  flex: 1,
                  overflowY: "auto",
                  border: "1px solid rgba(0,0,0,0.12)",
                  borderRadius: 1,
                  p: 1,
                }}
              >
                {plannerRunsLoading && (
                  <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                    <CircularProgress size={20} />
                  </Box>
                )}

                {!plannerRunsLoading && (!plannerRuns || plannerRuns.length === 0) && (
                  <Typography variant="caption" color="text.secondary">
                    No planner runs linked to this Full Change.
                  </Typography>
                )}

                {(plannerRuns || []).map((r) => {
                  const guardStatus = (r.status || "DRAFT").toUpperCase();
                  const notApproved = guardStatus !== "APPROVED";

                  return (
                    <Box
                      key={r.id}
                      sx={{
                        p: 1,
                        borderBottom: "1px solid rgba(0,0,0,0.08)",
                      }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        Run #{r.id} — {r.module || ""}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {r.status ? `Status: ${r.status}` : ""}
                        {r.planner_session_id !== null && r.planner_session_id !== undefined
                          ? `${r.status ? " • " : ""}Session: ${r.planner_session_id}`
                          : ""}
                        {r.created_at ? ` • Created: ${r.created_at}` : ""}
                      </Typography>

                      {notApproved && (
                        <Typography
                          variant="caption"
                          color="warning.main"
                          sx={{ display: "block", mt: 0.5 }}
                        >
                          Not approved — planner output is advisory only.
                        </Typography>
                      )}

                      {r.goal_text && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: "block" }}
                        >
                          {String(r.goal_text).slice(0, 200)}
                        </Typography>
                      )}
                    </Box>
                  );
                })}
              </Box>
            </>
          )}
        </Paper>
      </Stack>
    </Box>
  );
};

export default FullChangePanel;